import React, { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { ARYA_SUPPORT_CHAT_ROUTE } from '@/lib/supportChat';
import { getMacAppVersion } from '@/lib/macAppVersion';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useModalStore } from '@/store';
import MobileReviewLink from '@/components/Reviews/MobileReviewLink';
import SettingsSearchTarget from './SettingsSearchTarget';

const GAB_AI_BASE = 'https://gab.ai';

function HelpLink({ path, label, onNavigate, style, children }) {
  const { colors } = useTheme();
  const [focused, setFocused] = useState(false);
  return <Pressable
    accessibilityRole="link"
    accessibilityLabel={label}
    {...(Platform.OS === 'web' ? { href: path } : {})}
    onPress={event => {
      if (Platform.OS === 'web') {
        // Keep the browser's open-in-new-tab and modifier-click behavior.
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button != null && event.button !== 0)) return;
        event.preventDefault();
      }
      onNavigate(path);
    }}
    onFocus={() => setFocused(true)}
    onBlur={() => setFocused(false)}
    style={({ hovered, pressed }) => [style, {
      backgroundColor: hovered || pressed ? colors.solid_color_tertiary : 'transparent',
      ...(Platform.OS === 'web' && focused ? { outlineStyle: 'solid', outlineWidth: 2, outlineOffset: 3, outlineColor: colors.text_color_primary } : {}),
    }]}
  >{children}</Pressable>;
}

function HelpSettings() {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { closeModal } = useModalStore(state => state.actions);
  const [width, setWidth] = useState(0);
  const [linkFailed, setLinkFailed] = useState(false);
  const isWide = width >= 620;

  const handleLinkPress = async path => {
    setLinkFailed(false);
    if (Platform.OS !== 'web') {
      try {
        await WebBrowser.openBrowserAsync(`${GAB_AI_BASE}${path}`, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
          dismissButtonStyle: 'done',
        });
      } catch (_) {
        setLinkFailed(true);
      }
    } else {
      // The shared panel can also be displayed inside the settings modal.
      closeModal();
      router.push(path);
    }
  };

  const resources = [
    { key: 'learn', path: '/learn', icon: 'graduation-cap', action: 'startLearning' },
    { key: 'documentation', path: '/docs', icon: 'book-open', action: 'browseDocumentation' },
  ];
  const support = [
    { key: 'contactSupport', path: '/contact', icon: 'comments' },
    { key: 'submitFeedback', path: '/feedback', icon: 'comment-dots' },
  ];
  const legal = [
    { title: t('settings.help.privacyPolicy'), path: '/privacy-policy' },
    { title: t('settings.help.termsOfService'), path: '/terms-of-service' },
    { title: t('settings.help.termsOfSale'), path: '/terms-of-sale' },
    { title: 'CCPA', path: '/ccpa' },
    { title: 'DMCA', path: '/dmca' },
  ];

  return <View onLayout={event => setWidth(event.nativeEvent.layout.width)}>
    <SettingsSearchTarget label={t('settings.help.askArya')} style={[styles.aryaCard, { backgroundColor: colors.solid_color_secondary }]}>
      <HelpLink path={ARYA_SUPPORT_CHAT_ROUTE} label={t('settings.help.askArya')}
        onNavigate={path => { closeModal(); router.push(path); }} style={[styles.aryaLink, isWide && styles.aryaLinkWide]}>
        <View style={isWide && styles.aryaCopyWide}>
          <View style={styles.aryaHeading}>
            <Icon name="comments" size={24} color={colors.text_color_primary} />
            <Text size="l" weight="bold" style={styles.aryaTitle}>{t('settings.help.askArya')}</Text>
          </View>
          <Text size="s" color="secondary" style={styles.aryaDescription}>{t('settings.help.askAryaSubtitle')}</Text>
        </View>
        <View style={[styles.aryaAction, { backgroundColor: colors.text_color_primary }]}>
          <Text size="s" weight="semibold" style={{ color: colors.solid_color_primary }}>{t('settings.help.startChat')}</Text>
          <Icon name="arrow-right" size={13} color={colors.solid_color_primary} />
        </View>
      </HelpLink>
    </SettingsSearchTarget>
    <View style={[styles.resources, isWide && styles.resourcesWide]}>
      {resources.map(resource => <SettingsSearchTarget key={resource.key} label={t(`settings.help.${resource.key}`)} style={styles.resourceTarget}>
        <HelpLink path={resource.path} label={t(`settings.help.${resource.key}`)} onNavigate={handleLinkPress}
          style={[styles.resource, { borderColor: colors.border_color_secondary }]}>
          <View style={styles.resourceHeading}>
            <Icon name={resource.icon} size={21} color={colors.text_color_primary} />
            <Text size="l" weight="bold" style={styles.resourceTitle}>{t(`settings.help.${resource.key}`)}</Text>
          </View>
          <Text size="s" color="secondary" style={styles.resourceDescription}>{t(`settings.help.${resource.key}Subtitle`)}</Text>
          <View style={styles.resourceAction}>
            <Text size="s" weight="semibold">{t(`settings.help.${resource.action}`)}</Text>
            <Icon name="arrow-right" size={13} color={colors.text_color_primary} />
          </View>
        </HelpLink>
      </SettingsSearchTarget>)}
    </View>

    <View style={styles.support}>
      <Text size="l" weight="bold" accessibilityRole="header" accessibilityLevel={2} style={styles.sectionHeading}>
        {t('settings.help.getHelp')}
      </Text>
      <View style={[styles.supportGroup, { borderColor: colors.border_color_secondary }]}>
        {support.map((item, index) => <SettingsSearchTarget key={item.key} label={t(`settings.help.${item.key}`)}>
          {index > 0 && <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />}
          <HelpLink path={item.path} label={t(`settings.help.${item.key}`)} onNavigate={handleLinkPress} style={styles.supportRow}>
            <View style={styles.supportIcon}><Icon name={item.icon} size={21} color={colors.text_color_secondary} /></View>
            <View style={styles.supportCopy}>
              <Text size="m" weight="semibold">{t(`settings.help.${item.key}`)}</Text>
              <Text size="s" color="secondary" style={styles.supportDescription}>{t(`settings.help.${item.key}Subtitle`)}</Text>
            </View>
            <Icon name="chevron-right" size={12} color={colors.text_color_secondary} />
          </HelpLink>
        </SettingsSearchTarget>)}
      </View>
    </View>

    <MobileReviewLink />
    {linkFailed && <Text size="s" color="secondary" accessibilityRole="alert" style={styles.linkError}>{t('settings.help.linkError')}</Text>}

    <View style={[styles.footer, { borderTopColor: colors.border_color_secondary }]}>
      <View style={styles.footerHeading}>
        <Text size="s" weight="semibold" color="secondary" accessibilityRole="header" accessibilityLevel={2}>{t('settings.help.legal')}</Text>
      </View>
      <View style={styles.legalLinks}>
        {legal.map(link => <SettingsSearchTarget key={link.path} label={link.title}>
          <HelpLink path={link.path} label={link.title} onNavigate={handleLinkPress} style={styles.legalLink}>
            <Text size="xs" color="secondary">{link.title}</Text>
          </HelpLink>
        </SettingsSearchTarget>)}
      </View>
      <Text size="xs" color="secondary" style={styles.trademarkNotice}>
        Gab AI® is a registered trademark of Gab AI, Inc.
      </Text>
      {Platform.OS === 'macos' && <SettingsSearchTarget label={t('settings.help.version')} style={styles.version}>
        <Text size="xs" color="secondary">{t('settings.help.version')} {getMacAppVersion()}</Text>
      </SettingsSearchTarget>}
    </View>
  </View>;
}

const styles = StyleSheet.create({
  aryaCard: { borderRadius: 16, marginBottom: 12 },
  aryaLink: { padding: 20, borderRadius: 16, alignItems: 'flex-start', gap: 16 },
  aryaLinkWide: { flexDirection: 'row', alignItems: 'center', gap: 24 },
  aryaCopyWide: { flex: 1, minWidth: 0 },
  aryaHeading: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  aryaTitle: { flexShrink: 1 },
  aryaDescription: { lineHeight: 20, marginTop: 8 },
  aryaAction: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, minHeight: 40, borderRadius: 10 },
  resources: { gap: 12, marginBottom: 24 },
  resourcesWide: { flexDirection: 'row', alignItems: 'stretch' },
  resourceTarget: { flex: 1, minWidth: 0 },
  resource: { flex: 1, borderWidth: 1, borderRadius: 16, padding: 18, alignItems: 'flex-start' },
  resourceHeading: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 8 },
  resourceTitle: { flexShrink: 1 },
  resourceDescription: { lineHeight: 20, marginBottom: 14 },
  resourceAction: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 'auto', paddingTop: 2 },
  support: { marginBottom: 20 },
  sectionHeading: { marginBottom: 10 },
  supportGroup: { borderWidth: 1, borderRadius: 16 },
  supportRow: { flexDirection: 'row', alignItems: 'center', gap: 14, paddingHorizontal: 18, paddingVertical: 14, minHeight: 72, borderRadius: 15 },
  supportIcon: { width: 28, alignItems: 'center' },
  supportCopy: { flex: 1, minWidth: 0, gap: 4 },
  supportDescription: { lineHeight: 19 },
  divider: { height: 1, marginLeft: 60, marginRight: 18 },
  linkError: { marginBottom: 24 },
  footer: { borderTopWidth: 1, paddingTop: 16 },
  footerHeading: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 },
  legalLinks: { flexDirection: 'row', flexWrap: 'wrap', columnGap: 18 },
  legalLink: { minHeight: 44, justifyContent: 'center', borderRadius: 6 },
  trademarkNotice: { marginTop: 8, lineHeight: 18 },
  version: { paddingTop: 12, paddingBottom: 4 },
});

export default React.memo(HelpSettings);
